import React, { useState, useEffect, useRef, useCallback } from 'react';
import './RecapViewerModal.css';

/**
 * RecapViewerModal - Read-only modal for viewing recap context injected into each chunk
 *
 * Features:
 * - Left sidebar listing all chunks with recap preview
 * - Main content area showing the selected chunk's full recap text
 * - Auto-scrolls to the initially selected chunk
 * - Previous/Next navigation
 * - Graceful handling of missing recap text (pre-feature graphs)
 */
const RecapViewerModal = ({
  isOpen,
  onClose,
  chunkResults = [],
  initialChunkIndex = 0,
}) => {
  const [selectedIndex, setSelectedIndex] = useState(initialChunkIndex);
  const sidebarRef = useRef(null);
  const activeItemRef = useRef(null);

  // Reset selected index when modal opens with a new initial index
  useEffect(() => {
    if (isOpen) {
      setSelectedIndex(initialChunkIndex);
    }
  }, [isOpen, initialChunkIndex]);

  // Auto-scroll sidebar to active item
  useEffect(() => {
    if (isOpen && activeItemRef.current && sidebarRef.current) {
      activeItemRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, [isOpen, selectedIndex]);

  const handleKeyDown = useCallback((e) => {
    if (!isOpen) return;
    if (e.key === 'Escape') {
      onClose();
    } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(0, prev - 1));
    } else if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(chunkResults.length - 1, prev + 1));
    }
  }, [isOpen, onClose, chunkResults.length]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  if (!isOpen) return null;

  const selectedChunk = chunkResults[selectedIndex] || null;
  const recapText = selectedChunk?.recap_text || null;
  const hasRecap = selectedChunk?.recap_injected && recapText;

  // Extract a short preview from recap text for the sidebar
  const getPreview = (cr) => {
    if (!cr.recap_text) {
      if (cr.recap_injected) return `${(cr.recap_chars / 1000).toFixed(1)}k chars (text not stored)`;
      return 'No recap';
    }
    // Strip [Recap] tags and get first meaningful line
    const cleaned = cr.recap_text
      .replace(/^\[Recap\]\s*/i, '')
      .replace(/\s*\[\/Recap\]\s*$/i, '')
      .trim();
    const firstLine = cleaned.split('\n').find(l => l.trim()) || '';
    return firstLine.substring(0, 60) + (firstLine.length > 60 ? '...' : '');
  };

  // Render recap text with highlighted [Recap] tags
  const renderRecapText = (text) => {
    if (!text) return null;
    const parts = text.split(/(\[Recap\]|\[\/Recap\])/i);
    return parts.map((part, i) => {
      if (/^\[\/? *Recap\]$/i.test(part)) {
        return <span key={i} className="recap-tag">{part}</span>;
      }
      return <span key={i}>{part}</span>;
    });
  };

  return (
    <div className="recap-modal-overlay" onClick={onClose}>
      <div className="recap-modal-content" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="recap-modal-header">
          <h2>Chunk Recap Viewer ({chunkResults.length} chunks)</h2>
          <button className="recap-modal-close" onClick={onClose}>&times;</button>
        </div>

        {/* Body */}
        <div className="recap-modal-body">
          {/* Sidebar */}
          <div className="recap-sidebar" ref={sidebarRef}>
            {chunkResults.map((cr, idx) => {
              const isActive = idx === selectedIndex;
              const hasChunkRecap = cr.recap_injected && cr.recap_text;
              return (
                <button
                  key={cr.chunk_id || idx}
                  ref={isActive ? activeItemRef : null}
                  className={`recap-sidebar-item ${isActive ? 'active' : ''} ${!hasChunkRecap ? 'no-recap' : ''}`}
                  onClick={() => setSelectedIndex(idx)}
                >
                  <span className="recap-sidebar-chunk-num">
                    Chunk {cr.chunk_index ?? idx}
                  </span>
                  <span className="recap-sidebar-preview">
                    {getPreview(cr)}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Main */}
          <div className="recap-main">
            {/* Navigation */}
            <div className="recap-nav">
              <span className="recap-nav-title">
                Chunk {selectedChunk?.chunk_index ?? selectedIndex}
                {selectedChunk?.recap_injected ? ' — Recap Injected' : ' — No Recap'}
              </span>
              <div className="recap-nav-buttons">
                <button
                  className="recap-nav-btn"
                  disabled={selectedIndex <= 0}
                  onClick={() => setSelectedIndex(prev => prev - 1)}
                >
                  ← Previous
                </button>
                <button
                  className="recap-nav-btn"
                  disabled={selectedIndex >= chunkResults.length - 1}
                  onClick={() => setSelectedIndex(prev => prev + 1)}
                >
                  Next →
                </button>
              </div>
            </div>

            {/* Recap content */}
            <div className="recap-text-area">
              {hasRecap ? (
                <>
                  <div className="recap-chunk-info">
                    <span className="recap-chunk-badge">
                      Chunk #{selectedChunk.chunk_index ?? selectedIndex}
                    </span>
                    <span className="recap-chunk-badge size">
                      {(recapText.length / 1000).toFixed(1)}k chars
                    </span>
                  </div>
                  <div className="recap-text-block">
                    {renderRecapText(recapText)}
                  </div>
                </>
              ) : (
                <div className="recap-empty">
                  <div className="recap-empty-message">
                    {selectedIndex === 0 ? (
                      <>
                        <span className="recap-empty-icon">1️⃣</span>
                        This is the first chunk — no recap is generated for it.
                        Recaps provide rolling context from previous chunks and are
                        injected starting from chunk 2 onward.
                      </>
                    ) : selectedChunk?.recap_injected && !recapText ? (
                      <>
                        <span className="recap-empty-icon">📋</span>
                        A recap was injected for this chunk ({(selectedChunk.recap_chars / 1000).toFixed(1)}k chars),
                        but the text was not stored. This graph was built before recap text
                        storage was enabled. Rebuild the graph to capture recap text.
                      </>
                    ) : (
                      <>
                        <span className="recap-empty-icon">—</span>
                        No recap was generated for this chunk.
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecapViewerModal;
